import { useState } from 'react';
import { Anchor, Button, Card, Container, Loader, Stack, Text, Title } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { BILLING_EMAIL } from '@/lib/constants/account';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import { useBillingPlan, usePaymentMethod } from '../../hooks/useBilling';
import { useUpdatePaymentMethod, useRequestReopenAccount } from '../../hooks/useBillingMutations';
import { CardEntryUtils } from '../../CardEntryUtils';
import { PaymentMethodForm } from '../billing/PaymentMethodForm';
import { StripeCardForm } from '../billing/StripeCardForm';
import type { PaymentMethodFormValues } from '../../schemas/billingSchemas';
import type { IBillingAccountViewData } from '@/lib/types/Billing';

/**
 * Full-screen takeover for inactive (closed) accounts — legacy
 * AccountClosed.component parity. Master-account users with billingWrite get
 * the self-serve path: update card → billing/request-reopen. Expired trials
 * are routed to the trial upgrade page instead (upgrading IS the reopen).
 * With FEATURE_FLAGS.billingCardEntryEnabled the card step is the embedded
 * Stripe form (ADR-010); the reopen request that follows is unchanged.
 */
export function AccountClosedPage() {
  const navigate = useNavigate();
  const { authData, customerInfo } = useAuth();
  const { billingPlan } = useBillingPlan();
  const { paymentMethod, isLoading: isPaymentMethodLoading } = usePaymentMethod();
  const updateMutation = useUpdatePaymentMethod();
  const reopenMutation = useRequestReopenAccount();

  const [authError, setAuthError] = useState<string | null>(null);
  // 'requested' = card updated + reopen request accepted; 'email_billing' =
  // card updated but the reopen request failed (legacy fallback copy).
  const [outcome, setOutcome] = useState<'requested' | 'email_billing' | null>(null);

  // Legacy gates: the reopen form is only for users logging into the master
  // account (not a sub-account login) who can write billing.
  const isSubaccountLogin = !!customerInfo?.customer.cfg.parentCId;
  const canReopen = !isSubaccountLogin && !!authData?.cpuser.permissions.billingWrite;
  const isTrial = billingPlan?.billingPlan.billingPlanType === 'trial';
  const useStripeCardEntry = FEATURE_FLAGS.billingCardEntryEnabled;
  // The Stripe path creates the billing account row on confirm when none exists
  // (contract §2.2); the legacy path needs one to post the card into.
  const canShowForm = useStripeCardEntry || !!paymentMethod;

  async function requestReopen() {
    try {
      await reopenMutation.mutateAsync();
      setOutcome('requested');
    } catch {
      // Card update succeeded — direct the user to billing for the reopen itself.
      setOutcome('email_billing');
    }
  }

  async function handleSubmit(values: PaymentMethodFormValues) {
    if (!paymentMethod) return;
    setAuthError(null);

    const updatedData: IBillingAccountViewData = {
      ...paymentMethod,
      fullName: values.fullName,
      orgName: values.orgName || null,
      address1: values.address1,
      city: values.city,
      region: values.region,
      postalCode: values.postalCode,
      countryIso2: values.countryIso2,
      cardDetails: {
        cardNum: values.cardNum,
        cardLastFour: values.cardNum.slice(-4),
        cardType: paymentMethod.cardDetails?.cardType ?? 'visa',
        cardMonth: values.cardMonth,
        cardYear: values.cardYear,
        cvv: values.cvv,
      },
    };

    try {
      const result = await updateMutation.mutateAsync({
        billingAccountId: paymentMethod.billingAccountId,
        data: updatedData,
      });

      if (result.authResult && result.authResult.responseCodeName !== 'approved') {
        setAuthError(result.authResult.reason || 'Payment was declined. Please check your card details.');
        return;
      }
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Failed to update payment method.');
      return;
    }

    await requestReopen();
  }

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg" align="center">
        <Title order={2}>This account is closed.</Title>

        {isTrial ? (
          <>
            <Text ta="center">Your trial has expired. To resume service, please upgrade.</Text>
            <Button
              color="green"
              size="md"
              onClick={() => navigate({ to: '/account/upgrade-trial' })}
            >
              Upgrade Now
            </Button>
          </>
        ) : outcome ? (
          <Card withBorder radius="md" padding="lg" w="100%">
            {outcome === 'requested' ? (
              <Text ta="center">
                We have received your request to resume service. Please allow up to 1 business
                day to complete your request.
              </Text>
            ) : (
              <Text ta="center">
                Your payment method was updated. Please email{' '}
                <Anchor href={`mailto:${BILLING_EMAIL}`}>{BILLING_EMAIL}</Anchor> and request
                the account be re-opened.
              </Text>
            )}
          </Card>
        ) : canReopen ? (
          isPaymentMethodLoading ? (
            <Loader />
          ) : canShowForm ? (
            <>
              <Text ta="center">To re-open and resume service, please complete the form below.</Text>
              <Card shadow="sm" padding="lg" radius="md" withBorder w="100%">
                {useStripeCardEntry ? (
                  <StripeCardForm
                    defaults={CardEntryUtils.billingDefaults(paymentMethod)}
                    onSaved={requestReopen}
                    submitLabel="Update & Request Re-Open"
                  />
                ) : (
                  paymentMethod && (
                    <PaymentMethodForm
                      paymentMethod={paymentMethod}
                      onSubmit={handleSubmit}
                      isPending={updateMutation.isPending || reopenMutation.isPending}
                      authError={authError}
                      submitLabel="Update & Request Re-Open"
                    />
                  )
                )}
              </Card>
            </>
          ) : (
            <Text ta="center">
              To re-open and resume service, please email{' '}
              <Anchor href={`mailto:${BILLING_EMAIL}`}>{BILLING_EMAIL}</Anchor>.
            </Text>
          )
        ) : (
          !isSubaccountLogin && (
            <Text ta="center">
              To re-open and resume service, please have an administrator or billing contact
              login instead.
            </Text>
          )
        )}
      </Stack>
    </Container>
  );
}
